import { useState } from 'react';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { isSelfServeTier } from '@traaker/shared';
import { useAuth } from '../../contexts/AuthContext';
import PlanSelectionDialog from './PlanSelectionDialog';

/**
 * Generic "you hit a limit" popup, reused as the upgrade call-to-action across
 * every basic-tier limitation (locked website-audit agent, task-creation quota,
 * "analyze more pages", News Watch quota, ...).
 *
 * SELF-SERVE ONLY: renders nothing for agency/enterprise. Their quotas are
 * contractual (-1 in the registry), so the self-serve plan grid would be
 * offering them a DOWNGRADE — and `/billing/checkout/self-serve` 400s on those
 * tiers, so clicking through was a guaranteed dead end. This is the single
 * choke point for the gate: every call site funnels through here, so no page
 * has to remember to check the tier itself.
 *
 * Two modes:
 *  - `openPlansDirectly` (default false): shows an explainer step first ("Oops...
 *    limit reached" + body), and its own "See plans" button opens
 *    `PlanSelectionDialog`. Use this when the caller doesn't already have a
 *    dedicated explainer (e.g. the pages-analysed card).
 *  - `openPlansDirectly=true`: skips the explainer entirely and opens
 *    `PlanSelectionDialog` immediately — use this when the caller already shows
 *    its own explanation (e.g. the tier-locked agent card), so clicking its
 *    "Upgrade to Pro" button doesn't bounce through a second, redundant popup.
 */
export default function QuotaUpgradeDialog({
  open,
  onClose,
  body,
  openPlansDirectly = false,
  highlightMetric,
}: {
  open: boolean;
  onClose: () => void;
  /** Body copy — defaults to a generic sentence when omitted. */
  body?: string;
  openPlansDirectly?: boolean;
  /** Metric key to highlight on every plan card (see PLAN_FEATURE_ROWS). */
  highlightMetric?: string;
}) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const [planOpen, setPlanOpen] = useState(false);

  const subscribed =
    tenant?.billingMode === 'stripe' && tenant?.billingStatus === 'active' && !!tenant?.stripeSubscriptionId;

  // Agency / enterprise: nothing to upsell here (see the header note).
  if (!isSelfServeTier(tenant?.tier)) return null;

  if (openPlansDirectly) {
    return (
      <PlanSelectionDialog
        open={open}
        onClose={onClose}
        currentTier={tenant?.tier || 'basic'}
        subscribed={subscribed}
        highlightMetric={highlightMetric}
      />
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              bgcolor: '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#D97706', fontSize: 30 }} />
          </Box>
          <Typography variant="h6" fontWeight={800}>
            {t('quotaUpgradeDialog.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
            {body || t('quotaUpgradeDialog.defaultBody')}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: 'center' }}>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>
              {t('quotaUpgradeDialog.later')}
            </Button>
            <Button
              variant="contained"
              onClick={() => setPlanOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
            >
              {t('quotaUpgradeDialog.upgrade')}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <PlanSelectionDialog
        open={planOpen}
        onClose={() => {
          setPlanOpen(false);
          onClose();
        }}
        currentTier={tenant?.tier || 'basic'}
        subscribed={subscribed}
        highlightMetric={highlightMetric}
      />
    </>
  );
}
